/**
 * DEV-335 — Progressiver Product Wizard (Spec-04 §6.5) für die Google-Standee-PDP.
 *
 * Reines Verhaltens-Layer, kein neuer State: liest denselben `variant:change`, den das Theme-eigene
 * `<variant-picker>` bereits auf dem Formular feuert (siehe `assets/theme.js`), sowie die native
 * Menge-/Setup-Eingabe. Ein Product Controller bleibt die einzige Zustandsquelle — dieser Custom
 * Element dupliziert weder Varianten-Resolution noch Preisberechnung, sondern liest nur mit.
 *
 * Mobile und Desktop: alle Schritte (1/2/4) starten und bleiben aufgeklappt — kein gegenseitiges
 * Zuklappen mehr. Shopper können Schritte manuell schließen, ohne dass Geschwister automatisch
 * kollabieren.
 *
 * Reload/Vor-Zurück: Variantenwahl ist bereits über die bestehende `update-url`-Logik der
 * `<variant-picker>` in der URL verankert. Menge und Einrichtungs-Wahl sind reine Komfort-Zustände und
 * werden zusätzlich in `sessionStorage` je Formular gehalten, damit ein Reload sie nicht verwirft.
 *
 * Multi-Standort (2026-09-09): Default ist „Später einrichten". „Vorkonfiguriert erhalten" macht das
 * Setup-Feld required und zeigt den Multi-Standort-Hinweis; nach ATC öffnet `up2-multi-location.js`
 * einen Dialog statt des Cart-Drawers.
 */

/*
 * The snippet that loads this file sits inside the product section, which Impact re-renders on
 * variant changes. The script therefore runs more than once per page, so everything lives in a
 * function scope: top-level `class` declarations would throw on the second evaluation.
 */
(function () {
  function up2WizardFormatMoney(cents, format) {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '{{amount}}';

    function withDelimiters(number, precision, thousands, decimal) {
      if (Number.isNaN(number)) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const decimals = parts[1] ? decimal + parts[1] : '';
      return `${whole}${decimals}`;
    }

    const match = formatString.match(placeholderRegex);
    let value;

    switch (match?.[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return formatString.replace(placeholderRegex, value);
  }

  function up2WizardDiscountRate(qty) {
    if (qty >= 20) return 20;
    if (qty >= 10) return 15;
    if (qty >= 5) return 10;
    if (qty >= 2) return 5;
    return 0;
  }

  class Up2PdpWizard extends HTMLElement {
    connectedCallback() {
      this.formId = this.getAttribute('form');
      this.form = document.forms[this.formId];
      this.moneyFormat = this.getAttribute('money-format') || '';
      this.requiredError = this.getAttribute('setup-required-error') || '';
      this.root = this.closest('.product-info') || document;
      this.storageKey = `up2-pdp-wizard:${this.formId}`;
      this.saveTemplate = this.root.querySelector('up2-qty-offers')?.getAttribute('save-template') || '';

      this.steps = [...this.root.querySelectorAll('[data-wizard-step]')];
      this.qtyInput = document.getElementById(`${this.formId}-quantity`);
      this.setupRadios = [...this.root.querySelectorAll('[data-setup-choice]')];
      this.setupField = this.root.querySelector('[data-setup-name-field]');
      this.setupLaterHint = this.root.querySelector('[data-setup-later-hint]');
      this.setupMultiHint = this.root.querySelector('[data-setup-multi-hint]');
      this.setupChoiceProperty = this.root.querySelector('[data-setup-choice-property]');
      this.setupNameInput = this.setupField?.querySelector(
        '[data-places-input] input, [data-places-input] textarea, input:not([type="hidden"]), textarea',
      );
      this.unitPriceCents = Number(this.root.querySelector('up2-qty-offers')?.getAttribute('unit-price') || 0);

      this._restoreState();

      // All wizard steps stay open by default (desktop + mobile). No mutual-close accordion —
      // shoppers can see Aufsteller / Einrichtung / Menge together without collapsing siblings.
      [1, 2, 4].forEach((number) => {
        const step = this._step(number);
        if (step) step.open = true;
      });

      if (this.form) {
        this._onVariantChange = (event) => this._updateFromVariant(event.detail?.variant);
        this.form.addEventListener('variant:change', this._onVariantChange);
        this._onInvalid = (event) => this._handleInvalid(event);
        this.form.addEventListener('invalid', this._onInvalid, true);
      }

      if (this.qtyInput) {
        this._onQtyChange = () => this._updateFromQuantity();
        this.qtyInput.addEventListener('input', this._onQtyChange);
        this.qtyInput.addEventListener('change', this._onQtyChange);
      }

      this._onSetupChange = () => this._updateSetupChoice();
      this._onSetupInput = () => {
        this._clearSetupValidity();
        if (this.setupNameInput?.required && !this.setupNameInput.value.trim() && this.requiredError) {
          this.setupNameInput.setCustomValidity(this.requiredError);
        }
        this._updateSetupChoice();
      };
      this.setupRadios.forEach((radio) => radio.addEventListener('change', this._onSetupChange));
      this.setupNameInput?.addEventListener('input', this._onSetupInput);

      this._onPopoverOptionClick = (event) => {
        const option = event.target.closest('.popover-listbox__option');
        option?.closest('x-popover[close-on-listbox-select]')?.hide();
      };
      this.root.addEventListener('click', this._onPopoverOptionClick);

      this._updateSetupChoice();
      this._updateFromQuantity();
      this._updateStep1Value();
    }

    disconnectedCallback() {
      if (this.form && this._onVariantChange) {
        this.form.removeEventListener('variant:change', this._onVariantChange);
      }
      if (this.form && this._onInvalid) {
        this.form.removeEventListener('invalid', this._onInvalid, true);
      }
      if (this.qtyInput && this._onQtyChange) {
        this.qtyInput.removeEventListener('input', this._onQtyChange);
        this.qtyInput.removeEventListener('change', this._onQtyChange);
      }
      this.setupRadios.forEach((radio) => radio.removeEventListener('change', this._onSetupChange));
      this.setupNameInput?.removeEventListener('input', this._onSetupInput);
      if (this._onPopoverOptionClick) {
        this.root.removeEventListener('click', this._onPopoverOptionClick);
      }
    }

    /**
     * Called after a successful preconfigured ATC when the shopper chooses "another location".
     * Keeps the variant, resets qty to 1, clears setup text / place id, focuses setup step.
     */
    prepareAnotherLocation() {
      if (this.qtyInput) {
        this.qtyInput.value = '1';
        this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const nowRadio = this.setupRadios.find((radio) => radio.value === 'now');
      if (nowRadio) {
        nowRadio.checked = true;
      }

      if (this.setupNameInput) {
        this.setupNameInput.value = '';
        this.setupNameInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.setupNameInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const places = this.setupField?.querySelector('up2-places-autocomplete');
      const placeId = places?.querySelector('[data-places-id]');
      if (placeId) {
        placeId.value = '';
        placeId.disabled = true;
      }
      const preview = places?.querySelector('[data-places-preview]');
      if (preview) {
        preview.hidden = true;
        preview.querySelector('[data-places-preview-link]')?.removeAttribute('href');
      }

      this._updateSetupChoice();
      this._updateFromQuantity();

      const step2 = this._step(2);
      if (step2) step2.open = true;
      this.setupField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      requestAnimationFrame(() => this.setupNameInput?.focus());
    }

    _restoreState() {
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
      } catch {
        saved = null;
      }
      if (!saved) return;

      if (saved.qty && this.qtyInput) {
        this.qtyInput.value = saved.qty;
      }
      if (saved.setupChoice) {
        this.setupRadios.forEach((radio) => {
          radio.checked = radio.value === saved.setupChoice;
        });
      }
    }

    _saveState() {
      const setupChoice = this.setupRadios.find((radio) => radio.checked)?.value;
      try {
        sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({
            qty: this.qtyInput?.value,
            setupChoice,
          }),
        );
      } catch {
        // sessionStorage may be unavailable — non-fatal.
      }
    }

    _step(number) {
      return this.steps.find((step) => step.getAttribute('data-wizard-step') === String(number));
    }

    _setStepValue(number, value) {
      const badge = this._step(number)?.querySelector('[data-wizard-step-value]');
      if (!badge) return;
      badge.textContent = value || '';
      badge.hidden = !value;
    }

    _updateFromVariant(variant) {
      this._updateStep1Value();

      if (variant) {
        this.unitPriceCents = Number(variant.price) || this.unitPriceCents;
      }

      this._updateFromQuantity();
    }

    _updateStep1Value() {
      const step1 = this._step(1);
      if (!step1) return;

      const values = [...step1.querySelectorAll('[data-option-selected-value]')]
        .map((el) => el.textContent?.trim())
        .filter(Boolean);
      const value = values.join(' · ');

      this._setStepValue(1, value);
      this.root.querySelectorAll('[data-wizard-summary-options]').forEach((el) => {
        el.textContent = value;
      });
    }

    _updateFromQuantity() {
      const qty = Math.max(1, Number(this.qtyInput?.value) || 1);
      const unitLabel = this._qtyLabel(qty);

      this._setStepValue(4, unitLabel);
      this._saveState();

      if (!this.unitPriceCents) return;

      const rate = up2WizardDiscountRate(qty);
      const grossCents = this.unitPriceCents * qty;
      const totalCents = Math.round((grossCents * (100 - rate)) / 100);
      const formattedTotal = up2WizardFormatMoney(totalCents, this.moneyFormat);

      this.root.querySelectorAll('[data-wizard-summary-qty]').forEach((el) => {
        el.textContent = unitLabel;
      });
      this.root.querySelectorAll('[data-wizard-summary-total]').forEach((el) => {
        el.textContent = formattedTotal;
      });

      if (this.saveTemplate) {
        const saveCents = grossCents - totalCents;
        const saveFormatted = up2WizardFormatMoney(saveCents, this.moneyFormat);
        const saveText = this.saveTemplate.replace('__AMOUNT__', saveFormatted).replace('__RATE__', String(rate));
        this.root.querySelectorAll('[data-wizard-summary-save]').forEach((el) => {
          el.textContent = saveText;
        });
        this.root.querySelectorAll('[data-wizard-summary-save-line]').forEach((el) => {
          el.hidden = rate === 0;
        });
      }
    }

    _qtyLabel(qty) {
      const wrapper = this.root.querySelector('up2-qty-offers');
      const template = wrapper?.querySelector(`[data-qty="1"] .up2-qty-offers__qty`)?.textContent?.trim();
      if (qty === 1 && template) return template;
      const otherTemplate = wrapper?.querySelector(`[data-qty="2"] .up2-qty-offers__qty`)?.textContent?.trim();
      if (otherTemplate) return otherTemplate.replace(/^\d+/, String(qty));
      return String(qty);
    }

    _clearSetupValidity() {
      if (!this.setupNameInput) return;
      this.setupNameInput.setCustomValidity('');
    }

    _syncSetupRequired(preconfigured) {
      if (!this.setupNameInput) return;

      if (preconfigured) {
        this.setupNameInput.required = true;
        this.setupNameInput.setAttribute('aria-required', 'true');
        if (this.requiredError) {
          // Keep native required bubble, but with our locale copy when empty.
          if (!this.setupNameInput.value.trim()) {
            this.setupNameInput.setCustomValidity(this.requiredError);
          } else {
            this.setupNameInput.setCustomValidity('');
          }
        }
      } else {
        this.setupNameInput.required = false;
        this.setupNameInput.removeAttribute('aria-required');
        this.setupNameInput.setCustomValidity('');
      }
    }

    _handleInvalid(event) {
      if (event.target !== this.setupNameInput) return;

      const step2 = this._step(2);
      if (step2) step2.open = true;
      this.setupField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    _updateSetupChoice() {
      const choice = this.setupRadios.find((radio) => radio.checked)?.value || 'later';
      const later = choice === 'later';
      const preconfigured = !later;

      if (this.setupField) {
        this.setupField.hidden = later;
      }
      if (this.setupLaterHint) {
        this.setupLaterHint.hidden = !later;
      }
      if (this.setupMultiHint) {
        this.setupMultiHint.hidden = later;
      }

      this._syncSetupRequired(preconfigured);

      // "Später einrichten" must not submit a stale name / Place ID typed before switching.
      if (this.setupNameInput) {
        this.setupNameInput.disabled = later;
      }
      const placeId = this.setupField?.querySelector('[data-places-id]');
      if (placeId) {
        placeId.disabled = later || !placeId.value;
      }

      const label = this.setupRadios
        .find((radio) => radio.value === choice)
        ?.closest('label')
        ?.textContent?.trim();
      this._setStepValue(2, label);

      if (this.setupChoiceProperty && label) {
        this.setupChoiceProperty.value = label;
      }

      this.root.querySelectorAll('[data-wizard-summary-setup]').forEach((el) => {
        if (preconfigured && this.setupNameInput?.value) {
          el.textContent = this.setupNameInput.value;
        } else {
          el.textContent = label || '';
        }
      });

      this._saveState();
    }
  }

  if (!customElements.get('up2-pdp-wizard')) {
    customElements.define('up2-pdp-wizard', Up2PdpWizard);
  }
})();
